/* global BigInt */
import { PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import {
    TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID,
    getAssociatedTokenAddressSync, createAssociatedTokenAccountIdempotentInstruction,
    createTransferCheckedInstruction, createTransferCheckedWithFeeInstruction,
    unpackMint, unpackAccount, getTransferFeeConfig, calculateEpochFee,
    getTransferHook, addExtraAccountMetasForExecute, getNonTransferable, getPausableConfig,
    getScaledUiAmountConfig, getInterestBearingMintConfigState,
    getExtensionTypes, getAccountTypeOfMintType, getAccountLen, ExtensionType,
} from '@solana/spl-token';

const ZERO = BigInt(0);
const U64_MAX = BigInt('18446744073709551615');
const NETWORKS = {
    '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d': 'mainnet-beta',
    'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG': 'devnet',
    '4uhcVJyU9pJkvQyS88uRDiswHXSCkY3zQawwpjk2NsNY': 'testnet',
};

export function parseTransferAmount(value, decimals) {
    const text = String(value ?? '').trim();
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 255) throw new Error('Token precision is unavailable.');
    if (text.length > 280 || !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) {
        throw new Error('Enter a positive amount using digits and a decimal point.');
    }
    const [whole, fraction = ''] = text.split('.');
    if (fraction.length > decimals) throw new Error(`This token supports at most ${decimals} decimal places.`);
    const raw = BigInt(whole || '0') * (BigInt(10) ** BigInt(decimals)) + BigInt(fraction.padEnd(decimals, '0') || '0');
    if (raw <= ZERO) throw new Error('Enter an amount greater than zero.');
    if (raw > U64_MAX) throw new Error('The amount exceeds the token transfer limit.');
    return raw;
}

export function formatTransferAmount(raw, decimals) {
    const padded = String(raw).padStart(decimals + 1, '0');
    if (!decimals) return padded;
    const fraction = padded.slice(-decimals).replace(/0+$/, '');
    return padded.slice(0, -decimals) + (fraction ? '.' + fraction : '');
}

export function transferExplorerUrl(signature, network) {
    if (!signature || !Object.values(NETWORKS).includes(network)) return '';
    return `https://explorer.solana.com/tx/${encodeURIComponent(signature)}${network === 'mainnet-beta' ? '' : '?cluster=' + network}`;
}

function assertWallet(wallet, sender, isCurrent) {
    const adapter = wallet?.wallet?.adapter || wallet?.adapter || wallet;
    if (!isCurrent() || !wallet?.connected || wallet.publicKey?.toBase58?.() !== sender ||
        adapter?.connected === false || adapter.publicKey?.toBase58?.() !== sender) {
        throw new Error('Wallet changed. Review the transfer again with your current wallet.');
    }
    if (typeof wallet.sendTransaction !== 'function') throw new Error('This wallet cannot send transactions.');
}

function safeLamports(value) {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error('Unable to read an exact SOL balance or fee. Please retry.');
    return BigInt(value);
}

export async function prepareWalletTransfer({ wallet, connection, token, recipientAddress, amount, isCurrent = () => true }) {
    const senderAddress = wallet?.publicKey?.toBase58?.();
    assertWallet(wallet, senderAddress, isCurrent);
    const sender = new PublicKey(senderAddress);
    let recipient;
    try {
        recipient = new PublicKey(String(recipientAddress || '').trim());
    } catch {
        throw new Error('Enter a valid recipient wallet address.');
    }
    if (recipient.equals(sender)) throw new Error('Choose a recipient other than your connected wallet.');
    const native = token.isNative || token.symbol === 'SOL';
    const transaction = new Transaction();
    let decimals = 9;
    let amountRaw;
    let tokenFeeRaw = ZERO;
    let rentLamports = ZERO;

    if (native) {
        amountRaw = parseTransferAmount(amount, decimals);
        transaction.add(SystemProgram.transfer({ fromPubkey: sender, toPubkey: recipient, lamports: amountRaw }));
    } else {
        const mint = new PublicKey(token.mint);
        const mintAccount = await connection.getAccountInfo(mint, 'confirmed');
        if (!mintAccount) throw new Error('This token mint was not found on the connected network.');
        const programId = mintAccount.owner;
        if (!programId.equals(TOKEN_PROGRAM_ID) && !programId.equals(TOKEN_2022_PROGRAM_ID)) {
            throw new Error('This mint does not belong to a supported token program.');
        }
        const mintInfo = unpackMint(mint, mintAccount, programId);
        if (!mintInfo.isInitialized) throw new Error('This token mint is not initialized.');
        if (getNonTransferable(mintInfo)) throw new Error('This token is non-transferable.');
        if (getPausableConfig(mintInfo)?.paused) throw new Error('Transfers for this token are currently paused.');
        if (getScaledUiAmountConfig(mintInfo) || getInterestBearingMintConfigState(mintInfo)) {
            throw new Error('This token uses adjusted balances. Send it with a wallet that supports that token format.');
        }
        decimals = mintInfo.decimals;
        amountRaw = parseTransferAmount(amount, decimals);
        const destination = getAssociatedTokenAddressSync(mint, recipient, true, programId);
        const [sourceAccounts, destinationInfo] = await Promise.all([
            connection.getTokenAccountsByOwner(sender, { mint }, 'confirmed'),
            connection.getAccountInfo(destination, 'confirmed'),
        ]);
        assertWallet(wallet, senderAddress, isCurrent);
        const accounts = sourceAccounts.value
            .map(({ pubkey, account }) => unpackAccount(pubkey, account, programId))
            .filter((account) => account.owner.equals(sender) && account.mint.equals(mint) &&
                account.isInitialized && !account.isFrozen && account.amount > ZERO)
            .sort((a, b) => a.amount > b.amount ? -1 : a.amount < b.amount ? 1 : 0);
        if (accounts.reduce((sum, account) => sum + account.amount, ZERO) < amountRaw) {
            throw new Error(`Insufficient spendable ${token.symbol} balance.`);
        }
        if (destinationInfo) {
            const destinationAccount = unpackAccount(destination, destinationInfo, programId);
            if (!destinationAccount.owner.equals(recipient) || !destinationAccount.mint.equals(mint) ||
                !destinationAccount.isInitialized || destinationAccount.isFrozen) {
                throw new Error('The recipient token account cannot receive this token.');
            }
        } else {
            transaction.add(createAssociatedTokenAccountIdempotentInstruction(sender, destination, recipient, mint, programId));
            const extensions = programId.equals(TOKEN_2022_PROGRAM_ID)
                ? [...getExtensionTypes(mintInfo.tlvData).map(getAccountTypeOfMintType)
                    .filter((type) => type !== ExtensionType.Uninitialized), ExtensionType.ImmutableOwner]
                : [];
            rentLamports = safeLamports(await connection.getMinimumBalanceForRentExemption(getAccountLen(extensions), 'confirmed'));
        }

        const feeConfig = getTransferFeeConfig(mintInfo);
        const epoch = feeConfig ? BigInt((await connection.getEpochInfo('confirmed')).epoch) : ZERO;
        const hook = getTransferHook(mintInfo);
        let remaining = amountRaw;
        for (const account of accounts) {
            if (remaining === ZERO) break;
            const part = account.amount < remaining ? account.amount : remaining;
            const fee = feeConfig ? calculateEpochFee(feeConfig, epoch, part) : ZERO;
            const instruction = feeConfig
                ? createTransferCheckedWithFeeInstruction(account.address, mint, destination, sender, part, decimals, fee, [], programId)
                : createTransferCheckedInstruction(account.address, mint, destination, sender, part, decimals, [], programId);
            if (hook && !hook.programId.equals(PublicKey.default)) {
                await addExtraAccountMetasForExecute(connection, instruction, hook.programId,
                    account.address, mint, destination, sender, part, 'confirmed');
            }
            transaction.add(instruction);
            tokenFeeRaw += fee;
            remaining -= part;
        }
    }
    assertWallet(wallet, senderAddress, isCurrent);
    const [latest, genesisHash] = await Promise.all([
        connection.getLatestBlockhash('confirmed'), connection.getGenesisHash(),
    ]);
    transaction.feePayer = sender;
    transaction.recentBlockhash = latest.blockhash;
    transaction.lastValidBlockHeight = latest.lastValidBlockHeight;
    // Check packet size before asking the wallet to sign an atomic, possibly multi-account transfer.
    try {
        transaction.serialize({ requireAllSignatures: false, verifySignatures: false });
    } catch {
        throw new Error('This transfer needs too many token accounts for one transaction. Try a smaller amount.');
    }
    const [fee, balance] = await Promise.all([
        connection.getFeeForMessage(transaction.compileMessage(), 'confirmed'),
        connection.getBalance(sender, 'confirmed'),
    ]);
    if (fee.value == null) throw new Error('Unable to estimate the network fee. Please review again.');
    const feeLamports = safeLamports(fee.value);
    if (safeLamports(balance) < (native ? amountRaw : ZERO) + rentLamports + feeLamports) {
        throw new Error(native ? 'Not enough SOL for the amount and network fee.' : 'Not enough SOL for network fees and recipient token-account creation.');
    }
    assertWallet(wallet, senderAddress, isCurrent);
    return {
        transaction, ...latest, senderAddress, recipientAddress: recipient.toBase58(),
        amountRaw, decimals, tokenFeeRaw, receivedRaw: amountRaw - tokenFeeRaw,
        feeLamports, rentLamports, network: NETWORKS[genesisHash] || 'custom',
        rpcEndpoint: connection.rpcEndpoint,
    };
}

export async function submitWalletTransfer({ wallet, connection, prepared, isCurrent = () => true, onSubmitted = () => {} }) {
    assertWallet(wallet, prepared.senderAddress, isCurrent);
    if (prepared.rpcEndpoint !== connection.rpcEndpoint) throw new Error('The network changed. Review the transfer again.');
    const height = await connection.getBlockHeight('confirmed');
    if (height > prepared.lastValidBlockHeight) throw new Error('This transfer preview expired. Review the transfer again.');
    assertWallet(wallet, prepared.senderAddress, isCurrent);
    // The only broadcast is through the user's selected wallet. Preflight stays enabled.
    const signature = await wallet.sendTransaction(prepared.transaction, connection, {
        skipPreflight: false, preflightCommitment: 'confirmed', maxRetries: 3,
    });
    const submitted = {
        signature, network: prepared.network, blockhash: prepared.blockhash,
        lastValidBlockHeight: prepared.lastValidBlockHeight, status: 'pending',
    };
    onSubmitted(submitted);
    try {
        const confirmation = await connection.confirmTransaction({
            signature, blockhash: prepared.blockhash, lastValidBlockHeight: prepared.lastValidBlockHeight,
        }, 'confirmed');
        return { ...submitted, status: confirmation.value.err ? 'failed' : 'confirmed' };
    } catch {
        // A confirmation outage does not establish that a submitted payment failed.
        return submitted;
    }
}

export async function checkWalletTransfer(connection, submitted) {
    const response = await connection.getSignatureStatuses([submitted.signature], { searchTransactionHistory: true });
    const status = response.value[0];
    if (status?.err) return { ...submitted, status: 'failed' };
    if (status && (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized' || status.confirmations === null)) {
        return { ...submitted, status: 'confirmed' };
    }
    if (!status && await connection.getBlockHeight('finalized') > submitted.lastValidBlockHeight) {
        return { ...submitted, status: 'expired' };
    }
    return { ...submitted, status: 'pending' };
}
